import { type ShipmentOrdersListExEntity } from '@/libs/api'
import { SHIPMENT_DELIVERY_STATUS_TYPE } from '@/libs/api/types'

/** 1回の操作でまとめられる「まとまり」の上限 */
export const MAX_BUNDLE_GROUP_COUNT = 2

/** 束ねの成立に必要な最小選択件数（1件だけの選択はまとめようがない） */
export const MIN_BUNDLE_MEMBER_COUNT = 2

/**
 * 選択行が構成する「まとまり」の数を数える。
 * まとめ済みの依頼は同じ束ねごとに1、まとめられていない依頼(単品)は何件でも全体で1と数える。
 */
export const countBundleGroups = (
  orders: Pick<ShipmentOrdersListExEntity, 'bundleId'>[]
): number => {
  const bundleIds = new Set<number>()
  let hasStandaloneOrder = false

  orders.forEach((order) => {
    if (order.bundleId === null) {
      hasStandaloneOrder = true
      return
    }
    bundleIds.add(order.bundleId)
  })

  return bundleIds.size + (hasStandaloneOrder ? 1 : 0)
}

/**
 * まとまりが3つ以上になる選択は誤操作とみなしてまとめを許可しない。
 * まとめ済み+単品・まとめ済み2つは可、まとめ済み2つ+単品は不可、単品だけなら件数を問わず可。
 */
export const exceedsBundleGroupLimit = (
  orders: Pick<ShipmentOrdersListExEntity, 'bundleId'>[]
): boolean => countBundleGroups(orders) > MAX_BUNDLE_GROUP_COUNT

/** 束ねを許可しない理由。UI 側の分岐・文言の出し分けに使う */
export type BundleBlockReason =
  | 'NOT_ENOUGH_ORDERS'
  | 'HAS_DISPATCHED_ORDER'
  | 'TOO_MANY_BUNDLE_GROUPS'
  | 'HAS_REVOKED_ORDER'

/**
 * 選択した依頼をまとめられるか（束ね可否の単一述語）。
 *
 * - 2件以上の選択であること（{@link MIN_BUNDLE_MEMBER_COUNT}）
 * - 配車済みの依頼が混ざっていないこと。ShipmentOrder に紐づく DispatchDetail は
 *   高々1件（ドメイン制約）なので配列が空かどうかで未配車を判定できる
 * - まとまり数が上限以内であること（{@link exceedsBundleGroupLimit}）
 * - 取消済みの依頼が混ざっていないこと（#4620 VOC_PJ-981）。束ねは「まとめて1台に
 *   積む」ための単位なので、もう運ばない依頼が混ざると束ね単位の一括配車で
 *   取消済みを巻き込んでしまう
 */
export const canBundleOrders = (
  orders: Pick<
    ShipmentOrdersListExEntity,
    'bundleId' | 'dispatchDetails' | 'deliveryStatus'
  >[]
): { canBundle: boolean; reason: BundleBlockReason | null } => {
  if (orders.length < MIN_BUNDLE_MEMBER_COUNT) {
    return { canBundle: false, reason: 'NOT_ENOUGH_ORDERS' }
  }
  if (orders.some((order) => order.dispatchDetails.length > 0)) {
    return { canBundle: false, reason: 'HAS_DISPATCHED_ORDER' }
  }
  if (exceedsBundleGroupLimit(orders)) {
    return { canBundle: false, reason: 'TOO_MANY_BUNDLE_GROUPS' }
  }
  if (
    orders.some(
      (order) => order.deliveryStatus === SHIPMENT_DELIVERY_STATUS_TYPE.REVOKED
    )
  ) {
    return { canBundle: false, reason: 'HAS_REVOKED_ORDER' }
  }
  return { canBundle: true, reason: null }
}
